import React, { useState } from "react";

import pluralize from "pluralize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import {
  useAsyncActionFlash,
  useSetFlash,
  useSetNextFlashLocation,
} from "providers/WithFlashes";
import Loading from "ui/Loading";
import UsageBar from "features/Billing/UsageBar";
import { PoweredByStripeLogo } from "ui/Icons";
import { formatCost } from "utils/format";
import { Currency } from "utils/currency";
import { useRoutes } from "utils/routes";

import {
  ActivatePlanQuery as ActivatePlanType,
  useActivatePlanQuery,
} from "./gql/Query.generated";
import {
  ActivatePlanMutationMutation as ActivatePlanMutationType,
  useActivatePlanMutationMutation,
} from "./gql/Mutation.generated";

import { CreditCardForm } from "../UpdateCreditCard";
import CreditCardInfo from "../CreditCardInfo";
import { BillingDetailsForm } from "../UpdateBillingDetails";
import BillingDetailsInfo from "../BillingDetailsInfo";
import Tip from "ui/Tip";

type PlanChoice =
  ActivatePlanType["getOrganizationBilling"]["planChoices"][number];

const ActivatePlan: React.FunctionComponent<{
  organizationSlug: string;
}> = ({ organizationSlug }) => {
  const { loading, error, data } = useActivatePlanQuery({
    variables: {
      organizationSlug,
    },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return (
    <ActivatePlanContents organizationSlug={organizationSlug} data={data} />
  );
};

const ActivatePlanContents: React.FunctionComponent<{
  organizationSlug: string;
  data: ActivatePlanType;
}> = ({ organizationSlug, data }) => {
  const billingInfo = data.getOrganizationBilling;

  const { planId } = useParams();
  const plan = data.getOrganizationBilling.planChoices
    .filter((plan) => plan.isSelfServe)
    .find((plan) => plan.id == planId);

  const { organizationSubscriptionActivate } = useRoutes();
  const navigate = useNavigate();
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const elements = useElements();
  const stripe = useStripe();
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [totalServersEntered, setTotalServersEntered] = useState(
    initialTotalServers(data, plan, planId),
  );
  const [showCreditCardForm, setShowCreditCardForm] = useState(
    !billingInfo.subscriptionCardType,
  );
  const [showBillingDetailsForm, setShowBillingDetailsForm] = useState(
    !billingInfo.billingAddressLine1,
  );
  const [cardHolderName, setCardHolderName] = useState("");
  const [billingCompanyName, setBillingCompanyName] = useState(
    billingInfo.billingCompanyName ?? "",
  );
  const [billingAddressLine1, setBillingAddressLine1] = useState(
    billingInfo.billingAddressLine1 ?? "",
  );
  const [billingAddressLine2, setBillingAddressLine2] = useState(
    billingInfo.billingAddressLine2 ?? "",
  );
  const [billingAddressZipCode, setBillingAddressZipCode] = useState(
    billingInfo.billingAddressZipCode ?? "",
  );
  const [billingAddressCity, setBillingAddressCity] = useState(
    billingInfo.billingAddressCity ?? "",
  );
  const [billingAddressState, setBillingAddressState] = useState(
    billingInfo.billingAddressState ?? "",
  );
  const [billingAddressCountry, setBillingAddressCountry] = useState(
    billingInfo.billingAddressCountry ?? "",
  );
  const [billingVatId, setBillingVatId] = useState(
    billingInfo.billingVatId ?? "",
  );
  const [billingEmail, setBillingEmail] = useState(
    billingInfo.billingEmail ?? "",
  );
  const { organizationSubscription } = useRoutes();

  const [
    activatePlan,
    { called: mutationCalled, loading: mutationLoading, error: mutationError },
  ] = useActivatePlanMutationMutation();

  useAsyncActionFlash({
    called: mutationCalled,
    loading: mutationLoading,
    error: mutationError?.message,
  });

  if (!plan) {
    return <Navigate to={organizationSubscription(organizationSlug)} />;
  }

  const billingCurrency = data.getOrganizationBilling
    .billingCurrency as Currency;

  const activateTitle = plan.isCurrent
    ? `Adjusting ${plan.name} Plan`
    : plan.isDowngrade
    ? `Downgrading To ${plan.name} Plan`
    : `Upgrading To ${plan.name} Plan`;

  const totalServersParsed = parseInt(totalServersEntered, 10);
  const newTotalServerCount = isNaN(totalServersParsed)
    ? plan.serversIncluded
    : totalServersParsed;

  const planSupportsExtraServers =
    plan.billingCurrencyServerOveragePrice != null;
  const extraServers = Math.max(newTotalServerCount - plan.serversIncluded, 0);
  const extraServerCost = extraServers * plan.billingCurrencyServerOveragePrice;
  const extraServerCostLabel = planSupportsExtraServers ? (
    formatCost(extraServerCost, billingCurrency)
  ) : (
    <>&mdash;</>
  );
  const totalCost = plan.billingCurrencyMonthlyPrice + extraServerCost;
  const totalCostLabel = formatCost(totalCost, billingCurrency);

  const hasServerOverage =
    Math.max(newTotalServerCount, plan.serversIncluded) <
    plan.serverCountForBilling;

  const activatePlanOnCompleted = (data: ActivatePlanMutationType) => {
    const flashMessage = plan.isCurrent
      ? `Successfully adjusted the ${plan.name} plan.`
      : plan.isDowngrade
      ? `Successfully changed to the ${plan.name} plan.`
      : `Upgraded to the ${plan.name} plan - thanks for upgrading!`;

    if (!data.activateBillingPlan.organizationBilling.paymentRequiresAction) {
      // 3D Secure not required
      setFlash("success", flashMessage);
      setNextFlashLocation(organizationSubscription(organizationSlug));
      navigate(organizationSubscription(organizationSlug));
      return;
    }

    // Handle 3D Secure
    setStripeLoading(true);
    stripe
      .confirmCardPayment(
        data.activateBillingPlan.organizationBilling.stripeClientSecret,
      )
      .then(({ paymentIntent, error }) => {
        setStripeLoading(false);
        if (error) {
          setFlash(
            "error",
            "Payment authentication failed. Please update to a different payment method or contact support.",
          );
        } else if (paymentIntent.status === "succeeded") {
          setFlash("success", flashMessage);
        }
        // Navigate to the organizationSubscription page regardless of the success/failure
        // as we don't have a good flow to retry 3D Secure here
        setNextFlashLocation(organizationSubscription(organizationSlug));
        navigate(organizationSubscription(organizationSlug));
      });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!window.stripeTestPaymentMethod && (!stripe || !elements)) {
      return;
    }

    const mutationVariables = {
      organizationSlug,
      newTotalServerCount,
      newPlanId: plan.id,
      billingCompanyName,
      billingAddressLine1,
      billingAddressLine2,
      billingAddressZipCode,
      billingAddressCity,
      billingAddressState,
      billingAddressCountry,
      billingVatId,
      billingEmail,
    };

    if (!showCreditCardForm) {
      activatePlan({
        variables: mutationVariables,
        onCompleted: activatePlanOnCompleted,
      });
      return;
    }

    setStripeError(null);
    setStripeLoading(true);

    const billingDetails = {
      name: cardHolderName,
      address: {
        line1: billingAddressLine1,
        line2: billingAddressLine2,
        postal_code: billingAddressZipCode,
        city: billingAddressCity,
        state: billingAddressState,
      } as { [key: string]: string },
    };
    // Stripe complains when passing empty country value, so we need to skip it altogether if not present
    if (billingAddressCountry !== "") {
      billingDetails["address"]["country"] = billingAddressCountry;
    }

    if (window.stripeTestPaymentMethod) {
      activatePlan({
        variables: {
          ...mutationVariables,
          paymentMethodId: window.stripeTestPaymentMethod,
        },
        onCompleted: activatePlanOnCompleted,
      });
      return;
    }

    stripe
      .createPaymentMethod({
        type: "card",
        card: elements.getElement("cardNumber"),
        billing_details: billingDetails,
      })
      .then(({ paymentMethod, error }) => {
        setStripeLoading(false);
        if (error) {
          setStripeError(error.message);
        } else {
          activatePlan({
            variables: {
              ...mutationVariables,
              paymentMethodId: paymentMethod.id,
            },
            onCompleted: activatePlanOnCompleted,
          });
        }
      });
  };

  const planAction = plan.isCurrent
    ? "Adjust"
    : plan.isDowngrade
    ? "Downgrade"
    : "Upgrade";
  const planActionInProgress = plan.isCurrent
    ? "Adjusting"
    : plan.isDowngrade
    ? "Downgrading"
    : "Upgrading";
  // Hack: we want to reflow the form into a single column on larger screens,
  // but the submit button should come last in the first column in both cases.
  // There's no good way to do that without the still-draft masonry layout (see
  // https://drafts.csswg.org/css-grid-3/Overview.bs ), so instead we add this
  // button in two places and hide it at opposite breakpoints.
  const activateSubmit = (
    <>
      <p className="mt-8">
        I hereby accept the{" "}
        <a href="https://pganalyze.com/terms">Terms of Service</a>.
      </p>
      <input
        type="submit"
        name="commit"
        value={
          stripeLoading || mutationLoading
            ? planActionInProgress + " account..."
            : planAction + " account"
        }
        disabled={stripeLoading || mutationLoading}
        className="btn btn-success"
      ></input>
    </>
  );

  const serverUsageSection = (
    <div>
      <div>
        <ServerUsageIcon hasOverage={hasServerOverage} />{" "}
        <span className="font-semibold">{plan.serverCountForBilling}</span>{" "}
        {pluralize("billable server", plan.serverCountForBilling, false)}{" "}
        currently reporting statistics
        <ReplicaBillingMultiplierTip
          replicaBillingMultiplier={plan.replicaBillingMultiplier}
          replicaWithRecentDataCount={
            data.getOrganizationDetails.replicaWithRecentDataCount
          }
        />
      </div>
      <UsageBar
        usage={plan.serverCountForBilling}
        limit={Math.max(newTotalServerCount, plan.serversIncluded)}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] md:gap-3">
        <div>
          <Panel title={activateTitle}>
            <PanelSection>
              <div className="grid grid-cols-[1fr_max-content]">
                <div>
                  <ul>
                    {plan.marketingLines.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
                <div className="text-right text-lg text-[#19152c] font-semibold">
                  {plan.formattedMonthlyPrice}/month
                </div>
              </div>
            </PanelSection>
          </Panel>
          <Panel
            title="Billable Servers"
            secondaryTitle={
              <a
                href="https://pganalyze.com/docs/accounts/billing"
                target="_blank"
              >
                What is a billable server?
              </a>
            }
          >
            {planSupportsExtraServers ? (
              <PanelSection>
                <div className="grid grid-cols-[1fr_3fr] gap-2 mb-2.5 items-end">
                  <div>
                    <label htmlFor="total-servers">Billable servers</label>
                    <ReplicaBillingMultiplierTip
                      replicaBillingMultiplier={plan.replicaBillingMultiplier}
                    />
                    <input
                      id="total-servers"
                      className="form-control max-w-[8rem]"
                      type="text"
                      value={totalServersEntered}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        if (!/^[0-9]{0,3}$/.test(newVal)) {
                          return;
                        }
                        setTotalServersEntered(newVal);
                      }}
                    />
                  </div>
                  {serverUsageSection}
                </div>
                <div className="text-sm text-[#19152c]">
                  {plan.serversIncluded} billable servers included in plan,{" "}
                  {plan.formattedServerOveragePrice} per additional billable
                  server / month
                </div>
              </PanelSection>
            ) : (
              <PanelSection>
                {serverUsageSection}
                {hasServerOverage && (
                  <>
                    Please upgrade to{" "}
                    <Link
                      to={organizationSubscriptionActivate(
                        organizationSlug,
                        "scale_v4",
                      )}
                    >
                      Scale
                    </Link>{" "}
                    to monitor multiple servers
                  </>
                )}
              </PanelSection>
            )}
          </Panel>
          <Panel title="Summary">
            <PanelSection>
              <div className="text-base text-[#19152c] leading-7 grid grid-cols-[1fr_max-content]">
                {planSupportsExtraServers ? (
                  <div>
                    {plan.name} plan (first {plan.serversIncluded} billable
                    servers)
                  </div>
                ) : (
                  <div>{plan.name} plan</div>
                )}
                <div className="text-right">{plan.formattedMonthlyPrice}</div>
                {planSupportsExtraServers && (
                  <>
                    <div>
                      {extraServers} additional{" "}
                      {pluralize("billable server", extraServers)} at{" "}
                      {plan.formattedServerOveragePrice} per billable server /
                      month
                    </div>
                    <div className="text-right">{extraServerCostLabel}</div>
                  </>
                )}
              </div>
            </PanelSection>
            <PanelSection>
              <div className="text-lg text-[#19152c] font-semibold grid grid-cols-2">
                <div>
                  Total for{" "}
                  {planSupportsExtraServers &&
                  newTotalServerCount < plan.serversIncluded ? (
                    <>
                      up to {plan.serversIncluded}{" "}
                      {pluralize("billable server", plan.serversIncluded)}
                    </>
                  ) : (
                    <>
                      {Math.max(newTotalServerCount, plan.serversIncluded)}{" "}
                      {pluralize(
                        "billable server",
                        Math.max(newTotalServerCount, plan.serversIncluded),
                      )}
                    </>
                  )}
                </div>
                <div className="text-right">{totalCostLabel}/month</div>
              </div>
            </PanelSection>
          </Panel>
          <div className="hidden md:block">{activateSubmit}</div>
        </div>
        <div>
          <Panel title="Billing Information">
            <PanelSection>
              {stripeError && (
                <p className="payment-errors alert-danger inline-block">
                  {stripeError}
                </p>
              )}
              {showCreditCardForm ? (
                <>
                  <div className="flex justify-between mb-1">
                    <h3 className="twh text-base">Credit card</h3>
                    <PoweredByStripeLogo />
                  </div>
                  <CreditCardForm
                    cardHolderName={cardHolderName}
                    setCardHolderName={setCardHolderName}
                  />
                </>
              ) : (
                <CreditCardInfo
                  organizationSlug={organizationSlug}
                  subscriptionCardType={
                    data.getOrganizationBilling.subscriptionCardType
                  }
                  subscriptionCardLast4={
                    data.getOrganizationBilling.subscriptionCardLast4
                  }
                  subscriptionCardExpMonth={
                    data.getOrganizationBilling.subscriptionCardExpMonth
                  }
                  subscriptionCardExpYear={
                    data.getOrganizationBilling.subscriptionCardExpYear
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setShowCreditCardForm(true);
                  }}
                />
              )}
              <div className="mt-4">
                {showBillingDetailsForm ? (
                  <>
                    <h3 className="twh text-base">Billing details</h3>
                    <BillingDetailsForm
                      billingCompanyName={billingCompanyName}
                      setBillingCompanyName={setBillingCompanyName}
                      billingAddressLine1={billingAddressLine1}
                      setBillingAddressLine1={setBillingAddressLine1}
                      billingAddressLine2={billingAddressLine2}
                      setBillingAddressLine2={setBillingAddressLine2}
                      billingAddressZipCode={billingAddressZipCode}
                      setBillingAddressZipCode={setBillingAddressZipCode}
                      billingAddressCity={billingAddressCity}
                      setBillingAddressCity={setBillingAddressCity}
                      billingAddressState={billingAddressState}
                      setBillingAddressState={setBillingAddressState}
                      billingAddressCountry={billingAddressCountry}
                      setBillingAddressCountry={setBillingAddressCountry}
                      billingVatId={billingVatId}
                      setBillingVatId={setBillingVatId}
                      billingEmail={billingEmail}
                      setBillingEmail={setBillingEmail}
                    />
                  </>
                ) : (
                  <BillingDetailsInfo
                    organizationSlug={organizationSlug}
                    billingAddressWithName={
                      data.getOrganizationBilling.billingAddressWithName
                    }
                    billingVatId={billingVatId}
                    billingEmail={billingEmail}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowBillingDetailsForm(true);
                    }}
                  />
                )}
              </div>
            </PanelSection>
          </Panel>
        </div>
      </div>
      <div className="block md:hidden">{activateSubmit}</div>
    </form>
  );
};

// When adjusting the plan the organization is already on, start from what they
// are already paying for. On a plan change there is no established price to
// preserve, so start from the target plan's minimum.
function initialTotalServers(
  data: ActivatePlanType,
  plan: PlanChoice | undefined,
  planId: string,
): string {
  if (planId === data.getOrganizationDetails.planInfo.subscriptionPlanId) {
    return String(data.getOrganizationDetails.serverLimit);
  }
  return String(plan?.serversIncluded ?? "0");
}

const ServerUsageIcon: React.FunctionComponent<{ hasOverage: boolean }> = ({
  hasOverage,
}) => {
  return (
    <FontAwesomeIcon
      className={hasOverage ? "text-[#C22426]" : "text-[#43962A]"}
      icon={hasOverage ? faExclamationTriangle : faCheckCircle}
    />
  );
};

export const ReplicaBillingMultiplierTip = ({
  replicaBillingMultiplier,
  replicaWithRecentDataCount,
}: {
  replicaBillingMultiplier: number;
  replicaWithRecentDataCount?: number;
}) => {
  const content =
    replicaWithRecentDataCount === undefined
      ? `Replicas should be counted as ${replicaBillingMultiplier} billable servers, with the total rounded up.`
      : `${pluralize(
          "replica",
          replicaWithRecentDataCount,
          true,
        )} were counted as ${pluralize(
          "billable server",
          Math.ceil(replicaWithRecentDataCount * replicaBillingMultiplier),
          true,
        )}. Each replica is counted as ${replicaBillingMultiplier} servers, with the total rounded up.`;

  return replicaBillingMultiplier != 1.0 ? (
    <>
      {" "}
      <Tip content={content} />
    </>
  ) : null;
};

export default ActivatePlan;
